'use client';

import React from 'react';

import { cn } from '@udecode/cn';
import { HEADING_KEYS } from '@udecode/plate-heading';
import { ParagraphPlugin } from '@udecode/plate/react';

import {
  type PlaceholderProps,
  createNodeHOC,
  createNodesHOC,
  usePlaceholderState,
  useEditorRef,
  useFocused,
  useSelected,
} from '@udecode/plate/react';

export const Placeholder = (props: PlaceholderProps) => {
  const { children, nodeProps, placeholder, element } = props;

  const { enabled } = usePlaceholderState(props);
  const editor = useEditorRef();
  const isFocused = useFocused();
  const isSelected = useSelected();

  const isLastNode = React.useMemo(() => {
    if (!editor || !element) return false;
    const lastNode = editor.children[editor.children.length - 1];
    return element === lastNode;
  }, [editor, element, editor.children]);

  const showPlaceholder = enabled && ((isFocused && isSelected) || (!isFocused && isLastNode));

  return React.Children.map(children, (child) => {
    return React.cloneElement(child, {
      className: child.props.className,
      nodeProps: {
        ...nodeProps,
        className: cn(
          showPlaceholder &&
            'before:absolute before:cursor-text before:opacity-30 before:content-[attr(placeholder)]'
        ),
        placeholder,
      },
    });
  });
};

export const withPlaceholder = createNodeHOC(Placeholder);

export const withPlaceholdersPrimitive = createNodesHOC(Placeholder);

export const withPlaceholders = (components: any) =>
  withPlaceholdersPrimitive(components, [
    {
      key: ParagraphPlugin.key,
      placeholder: 'Type something...',
      hideOnBlur: false,
      query: {
        maxLevel: 1,
      },
    },
    {
      key: HEADING_KEYS.h1,
      hideOnBlur: false,
      placeholder: 'Untitled',
    },
  ]);
